import { type ComponentType, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronButton } from '@/shared/components';
import PhoneIcon from '@/shared/assets/phone-icon.svg?react';
import SmsIcon from '@/shared/assets/sms-icon.svg?react';
import EmailIcon from '@/shared/assets/email-icon.svg?react';
import { cn } from '@/shared/utils';
import {
  MFA_IDENTITY_TYPES,
  type MfaIdentity,
  type MFAIdentityType,
} from '@/entities/mfa-identity';

type Props = {
  className?: string;
  availableIdentities: Array<MfaIdentity>;
  onSelectIdentity: (identity: MFAIdentityType) => void;
  isLoading?: boolean;
};

type MfaIdentityOption = {
  type: MFAIdentityType;
  icon: ComponentType<{ className?: string }>;
  displayName: string;
};

const MfaIdentitiesList = ({
  availableIdentities,
  onSelectIdentity,
  className,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const [selectedIdentity, setSelectedIdentity] =
    useState<MFAIdentityType | null>(null);

  const mfaIdentities: MfaIdentityOption[] = useMemo(
    () => [
      {
        type: MFA_IDENTITY_TYPES.GOOGLE_AUTHENTICATOR,
        icon: PhoneIcon,
        displayName: t('authPages.mfaMethodsList.twoFACode'),
      },
      {
        type: MFA_IDENTITY_TYPES.GOOGLE_AUTHENTICATOR_UPPERCASE,
        icon: PhoneIcon,
        displayName: t('authPages.mfaMethodsList.twoFACode'),
      },
      {
        type: MFA_IDENTITY_TYPES.PHONE,
        icon: SmsIcon,
        displayName: t('authPages.mfaMethodsList.smsCode'),
      },
      {
        type: MFA_IDENTITY_TYPES.EMAIL,
        icon: EmailIcon,
        displayName: t('authPages.mfaMethodsList.emailCode'),
      },
    ],
    [t]
  );

  const identitiesToDisplay = mfaIdentities.filter(identity =>
    availableIdentities.some(
      availableIdentity =>
        availableIdentity.name === identity.type &&
        availableIdentity.isEnabled !== false
    )
  );

  return (
    <div className={cn('space-y-[1.6rem]', className)}>
      {identitiesToDisplay.map(identity => {
        const IconComponent = identity.icon;
        return (
          <ChevronButton
            icon={IconComponent}
            loading={identity.type === selectedIdentity && isLoading}
            key={identity.type}
            onClick={() => {
              setSelectedIdentity(identity.type);
              onSelectIdentity(identity.type);
            }}>
            <p className="medium4 whitespace-normal">{identity.displayName}</p>
          </ChevronButton>
        );
      })}
    </div>
  );
};

export { MfaIdentitiesList };
